import React from "react";
import {
  Box,
  AHText,
  AHTeamIconCircle,
  useLayoutContext,
  AHSeparator,
} from "arkh";
import { TaskExecutionNumber } from "../RoutineOccasion/TaskExecution/TaskExecutionComponents/TaskExecutionNumber";
import { useSizeClass } from "../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";
import Color from "color";
import { useIntlTokens } from "../../components/mobile/useIntlTokens";
import { graphql, useFragment } from "react-relay";
import { KPIListInputItem_taskExecution$key } from "./__generated__/KPIListInputItem_taskExecution.graphql";
import { KPIListInputItem_routineOccasionSection$key } from "./__generated__/KPIListInputItem_routineOccasionSection.graphql";
import { KPIListItemHeight } from "./KPIListItemHeight";

type Props = {
  occasionSection: KPIListInputItem_routineOccasionSection$key;
  taskExecution: KPIListInputItem_taskExecution$key;
  sequenceNumber: number;
  routineId: string;
  lastItem?: boolean;
  tabIndex?: number;
};

export const KPIListInputItem = React.memo(KPIListInputItemC);
function KPIListInputItemC(props: Props) {
  const intl_tokens = useIntlTokens();
  const layout = useLayoutContext();

  const { section, sectionVersion } = useFragment(
    graphql`
      fragment KPIListInputItem_routineOccasionSection on RoutineOccasionSection {
        sectionVersion {
          title
        }
        section {
          id
          title
          assignedTeam {
            name
            icon
            color
            isPrivate
          }
        }
      }
    `,
    props.occasionSection
  );

  const sectionTitle = sectionVersion.title || section.assignedTeam?.name;
  const isCompact = useSizeClass().width === SizeClass.COMPACT;
  const isSubSection = !(
    sectionVersion.title === null && section.assignedTeam?.icon
  );

  const taskExecution = useFragment(
    graphql`
      fragment KPIListInputItem_taskExecution on TaskExecutionNumber {
        id
        ...TaskExecutionNumberComponent_TaskExecutionNumber
        goals {
          lessThan
          greaterThan
        }
      }
    `,
    props.taskExecution
  );

  return (
    <>
      <Box
        spaceHorizontal={isCompact ? "small" : "xlarge"}
        style={{
          flexDirection: "row",
          alignItems: "center",
          backgroundColor: Color(
            section.assignedTeam?.color ?? "rgb(255,255,255)"
          )
            .lightness(99)
            .toString(),
          height: KPIListItemHeight,
        }}
        key={taskExecution.id}
        color="negative"
      >
        <Box spaceRight="small" style={{ flexDirection: "column" }}>
          {!isSubSection ? (
            <AHTeamIconCircle
              type={section.assignedTeam?.icon}
              color={section.assignedTeam?.color}
              isPrivate={section.assignedTeam?.isPrivate}
              size="medium"
            />
          ) : (
            <Box
              style={{
                height: KPIListItemHeight,
                width: layout.circle.medium,
                justifyContent: "center",
                alignItems: "center",
              }}
              spaceBottom={props.lastItem ? "large" : "none"}
            >
              <Box
                style={{
                  height: "100%",
                  width: 4,
                  backgroundColor: section.assignedTeam?.color ?? "blue",
                }}
                cornerRadiusBottomLeft={props.lastItem ? "large" : "none"}
                cornerRadiusBottomRight={props.lastItem ? "large" : "none"}
              />
            </Box>
          )}
        </Box>
        <Box style={{ flex: 1, height: KPIListItemHeight }}>
          <Box style={{ flexDirection: "row", alignItems: "center", flex: 1 }}>
            <AHText
              style={{ flex: 1 }}
              numberOfLines={1}
              adjustsFontSizeToFit
              minimumFontScale={0.3}
              type={isSubSection ? "subtitle" : "title-small"}
            >
              {sectionTitle}
            </AHText>
            <Box
              spaceLeft="small"
              spaceVertical="xsmall"
              style={{
                flexDirection: "column",
                justifyContent: "flex-end",
              }}
            >
              <TaskExecutionNumber
                hideGoals
                hideButtons
                spaceVertical="none"
                taskExecution={taskExecution}
                onNext={() => {}}
                tabIndex={props.tabIndex}
              />
              {taskExecution.goals ? (
                <AHText
                  style={{
                    alignSelf: "flex-end",
                    position: "absolute",
                    bottom: -10,
                  }}
                  type="info-small"
                  color="washedText"
                >
                  {taskExecution.goals.greaterThan !== null
                    ? `${intl_tokens.Target}: ${intl_tokens.Over} ${taskExecution.goals.greaterThan}`
                    : taskExecution.goals.lessThan !== null
                      ? `${intl_tokens.Target}: ${intl_tokens.Under} ${taskExecution.goals.lessThan}`
                      : ""}
                </AHText>
              ) : null}
            </Box>
          </Box>
          {props.lastItem ? null : <AHSeparator />}
        </Box>
      </Box>
    </>
  );
}
